import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { DOC_GROUPS, DOCS, docPath } from "@/content/docs";

// Renders one documentation article, or the docs home when no slug is given.
// Article text lives in content/docs.js; this file only knows how to draw
// its block types (p, list, steps, table, code, note) and the shell around
// them: a grouped sidebar on the left, the article in the middle, and the
// article's own sections on the right.

export const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const groupOf = (slug) => DOC_GROUPS.find((g) => g.slugs.includes(slug));

// ── Blocks ───────────────────────────────────────────────────────────────────
//
// Block, OnThisPage and slugify are exported because pages/GuidesPage.jsx
// renders the same block vocabulary from src/content/guides.js. Shared rather
// than copied: a block type that renders one way here and another way there is
// a difference a reader would see and a crawler would be served twice.

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard blocked — the text is still selectable */ }
  };
  return (
    <div className="relative group/code border border-white/10 bg-neutral-950">
      <button
        type="button"
        onClick={copy}
        className="absolute top-2 right-2 font-mono-t text-[10px] uppercase tracking-widest px-2 py-1 border border-white/20 text-neutral-300 hover:text-white hover:border-white/50 transition-colors"
      >
        {copied ? "Copied" : "Copy"}
      </button>
      <pre className="text-neutral-100 text-[13px] leading-relaxed p-4 pr-20 overflow-x-auto">
        <code>{code}</code>
      </pre>
    </div>
  );
}

// A console screenshot. Detail in these is small at article width, so the
// whole figure is a button that opens the image full-screen.
function Screenshot({ src, alt, caption }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open]);

  return (
    <figure className="my-2">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Enlarge screenshot: ${alt}`}
        className="group block w-full relative border border-white/10 bg-white/[0.02] hover:border-[#5B8CFF] transition-colors cursor-zoom-in"
      >
        <img src={src} alt={alt} loading="lazy" decoding="async" className="w-full h-auto block" />
        <span className="absolute bottom-2 right-2 font-mono-t text-[10px] uppercase tracking-widest bg-neutral-900/85 text-white px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
          Click to enlarge
        </span>
      </button>
      {caption && <figcaption className="mt-2 text-sm text-neutral-500">{caption}</figcaption>}

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[100] bg-neutral-950/90 p-4 md:p-10 flex items-center justify-center cursor-zoom-out"
        >
          <img src={src} alt={alt} className="max-w-full max-h-full object-contain border border-white/10" />
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="absolute top-4 right-4 font-mono-t text-[11px] uppercase tracking-widest text-neutral-300 border border-white/25 px-3 py-1.5 hover:text-white hover:border-white/60"
          >
            Close
          </button>
        </div>
      )}
    </figure>
  );
}

export function Block({ b }) {
  if (b.p) return <p>{b.p}</p>;

  if (b.list) {
    return (
      <ul className="space-y-2.5">
        {b.list.map((x, i) => (
          <li key={i} className="flex gap-3">
            <span aria-hidden className="mt-[0.6em] flex-none w-1.5 h-1.5 bg-[#0047FF]" />
            <span>{x}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (b.steps) {
    return (
      <ol className="space-y-0 border-l border-white/10 ml-3">
        {b.steps.map((x, i) => (
          <li key={i} className="relative pl-8 pb-5 last:pb-0">
            <span className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-neutral-900 text-white font-mono-t text-[11px] grid place-items-center">
              {i + 1}
            </span>
            <span className="block pt-0.5">{x}</span>
          </li>
        ))}
      </ol>
    );
  }

  if (b.table) {
    return (
      <div className="overflow-x-auto border border-white/10 bg-white/[0.02]">
        <table className="w-full text-sm text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10">
              {b.table.head.map((h) => (
                <th key={h} className="px-4 py-3 font-mono-t text-[10px] uppercase tracking-widest text-neutral-500 font-semibold whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.table.rows.map((r, i) => (
              <tr key={i} className="border-t border-white/10 align-top">
                {r.map((c, j) => (
                  <td key={j} className={`px-4 py-3 ${j === 0 ? "text-white font-medium whitespace-nowrap" : "text-neutral-400"}`}>
                    {c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (b.img) return <Screenshot {...b.img} />;

  if (b.code) return <CodeBlock code={b.code} />;

  if (b.note) {
    return (
      <div className="flex gap-3 border border-[#0047FF]/25 bg-[#0047FF]/[0.05] px-4 py-3.5">
        <span aria-hidden className="flex-none mt-0.5 w-5 h-5 rounded-full bg-[#0047FF] text-white text-[12px] font-semibold grid place-items-center leading-none">i</span>
        <div className="text-neutral-300 text-[15px]">{b.note}</div>
      </div>
    );
  }

  return null;
}

// ── Sidebar ──────────────────────────────────────────────────────────────────

function SidebarNav({ current, onNavigate }) {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const groups = DOC_GROUPS
    .map((g) => ({
      ...g,
      slugs: g.slugs.filter(
        (s) => !term || DOCS[s].title.toLowerCase().includes(term) || DOCS[s].summary.toLowerCase().includes(term)
      ),
    }))
    .filter((g) => g.slugs.length);

  return (
    <div className="space-y-6">
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filter articles"
        aria-label="Filter articles"
        className="w-full bg-white/[0.03] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-neutral-400 focus:outline-none focus:border-[#5B8CFF]"
      />
      <nav aria-label="Documentation" className="space-y-6">
        {groups.map((g) => (
          <div key={g.title}>
            <p className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-400 mb-2">{g.title}</p>
            <ul className="border-l border-white/10">
              {g.slugs.map((s) => {
                const active = s === current;
                return (
                  <li key={s}>
                    <Link
                      to={docPath(s)}
                      onClick={onNavigate}
                      aria-current={active ? "page" : undefined}
                      className={`block -ml-px border-l pl-3 py-1.5 text-sm transition-colors ${
                        active
                          ? "border-[#0047FF] text-white font-semibold"
                          : "border-transparent text-neutral-500 hover:text-white hover:border-white/30"
                      }`}
                    >
                      {DOCS[s].title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {!groups.length && <p className="text-sm text-neutral-500">No articles match “{q}”.</p>}
      </nav>
    </div>
  );
}

// ── On this page ─────────────────────────────────────────────────────────────

export function OnThisPage({ sections }) {
  const [active, setActive] = useState(sections[0] && slugify(sections[0].h));
  const ticking = useRef(false);

  useEffect(() => {
    const ids = sections.map((s) => slugify(s.h));
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        ticking.current = false;
        let current = ids[0];
        for (const id of ids) {
          const el = document.getElementById(id);
          if (el && el.getBoundingClientRect().top <= 140) current = id;
        }
        setActive(current);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections]);

  if (sections.length < 2) return null;

  return (
    <div className="hidden xl:block sticky top-28 self-start">
      <p className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-400 mb-3">On this page</p>
      <ul className="border-l border-white/10 space-y-0.5">
        {sections.map((s) => {
          const id = slugify(s.h);
          return (
            <li key={id}>
              <a
                href={`#${id}`}
                className={`block -ml-px border-l pl-3 py-1 text-[13px] leading-snug transition-colors ${
                  active === id
                    ? "border-[#0047FF] text-white"
                    : "border-transparent text-neutral-500 hover:text-white"
                }`}
              >
                {s.h}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Docs home ────────────────────────────────────────────────────────────────

function DocsHome() {
  return (
    <div data-testid="docs-home">
      <section className="relative pt-32 md:pt-36 pb-10 border-b border-white/10 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }} />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-4">Documentation</p>
          <h1 className="font-heading font-medium tracking-tight leading-[1.02] text-4xl md:text-5xl text-white max-w-3xl">
            OwnPBX help center
          </h1>
          <p className="mt-5 text-lg text-neutral-400 max-w-2xl leading-relaxed">
            Set up your phone system, connect phones, build AI voice agents and integrate with our API.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["getting-started", "connect-phones", "phone-numbers"].map((s) => (
              <Link
                key={s}
                to={docPath(s)}
                className="border border-white/15 bg-white/[0.02] px-4 py-2 text-sm text-white hover:border-[#5B8CFF] hover:text-[#5B8CFF] transition-colors"
              >
                {DOCS[s].title} →
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-12 md:columns-2 lg:columns-3 gap-x-12">
          {DOC_GROUPS.map((g) => (
            <div key={g.title} className="break-inside-avoid mb-12">
              <div className="flex items-baseline justify-between border-b border-white/10 pb-2">
                <h2 className="font-heading font-medium text-lg text-white">{g.title}</h2>
                <span className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-400">
                  {g.slugs.length} {g.slugs.length === 1 ? "article" : "articles"}
                </span>
              </div>
              <ul className="mt-4 space-y-4">
                {g.slugs.map((s) => (
                  <li key={s}>
                    <Link to={docPath(s)} className="group block">
                      <span className="text-white font-medium group-hover:text-[#5B8CFF] transition-colors">
                        {DOCS[s].title}
                      </span>
                      <span className="block text-sm text-neutral-500 mt-1 leading-relaxed">{DOCS[s].summary}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ── Article ──────────────────────────────────────────────────────────────────

export default function DocsPage({ slug }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const doc = slug ? DOCS[slug] : null;

  const { prev, next } = useMemo(() => {
    const flat = DOC_GROUPS.flatMap((g) => g.slugs);
    const i = flat.indexOf(slug);
    return { prev: i > 0 ? flat[i - 1] : null, next: i >= 0 && i < flat.length - 1 ? flat[i + 1] : null };
  }, [slug]);

  if (slug && !DOCS[slug]) return <Navigate to="/docs" replace />;
  if (!slug) return <DocsHome />;

  const group = groupOf(slug);

  return (
    <div data-testid={`doc-${slug}`} className="pt-24 md:pt-28 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid lg:grid-cols-[240px_minmax(0,1fr)] xl:grid-cols-[240px_minmax(0,1fr)_200px] gap-x-12">
        {/* Sidebar — desktop */}
        <aside className="hidden lg:block sticky top-28 self-start max-h-[calc(100vh-8rem)] overflow-y-auto pb-8 pr-2">
          <Link to="/docs" className="block text-sm text-neutral-500 hover:text-white mb-5">
            ← All documentation
          </Link>
          <SidebarNav current={slug} />
        </aside>

        <article className="min-w-0 max-w-[46rem]">
          {/* Sidebar — mobile */}
          <div className="lg:hidden mb-8">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              className="w-full flex items-center justify-between border border-white/15 bg-white/[0.02] px-4 py-2.5 text-sm text-white"
            >
              <span>Browse documentation</span>
              <span aria-hidden className={`transition-transform ${menuOpen ? "rotate-180" : ""}`}>▾</span>
            </button>
            {menuOpen && (
              <div className="border border-t-0 border-white/15 bg-white/[0.02] p-4">
                <SidebarNav current={slug} onNavigate={() => setMenuOpen(false)} />
              </div>
            )}
          </div>

          <nav aria-label="Breadcrumb" className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-400 flex flex-wrap items-center gap-2">
            <Link to="/docs" className="hover:text-white transition-colors">Docs</Link>
            {group && (<><span aria-hidden>/</span><span className="text-neutral-500">{group.title}</span></>)}
          </nav>

          <h1 className="mt-4 font-heading font-medium tracking-tight leading-[1.05] text-3xl md:text-[2.6rem] text-white">
            {doc.title}
          </h1>
          <p className="mt-4 text-lg text-neutral-400 leading-relaxed">{doc.summary}</p>
          <hr className="mt-8 border-white/10" />

          {doc.sections.map((s, i) => {
            const id = slugify(s.h);
            return (
              <section key={s.h} id={id} className={`scroll-mt-28 ${i ? "mt-12" : "mt-10"}`}>
                <h2 className="group font-heading font-medium tracking-tight text-[1.6rem] text-white">
                  {s.h}
                  <a
                    href={`#${id}`}
                    aria-label={`Link to ${s.h}`}
                    className="ml-2 align-middle text-[#5B8CFF] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-base"
                  >
                    #
                  </a>
                </h2>
                <div className="mt-4 space-y-5 text-[15.5px] text-neutral-400 leading-[1.75]">
                  {s.blocks.map((b, j) => <Block key={j} b={b} />)}
                </div>
              </section>
            );
          })}

          {(prev || next) && (
            <nav aria-label="More articles" className="mt-16 grid sm:grid-cols-2 gap-4">
              {prev ? (
                <Link to={docPath(prev)} className="group border border-white/10 bg-white/[0.02] p-4 hover:border-[#5B8CFF] transition-colors">
                  <span className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-400">← Previous</span>
                  <span className="block mt-1.5 text-white font-medium group-hover:text-[#5B8CFF] transition-colors">{DOCS[prev].title}</span>
                </Link>
              ) : <span className="hidden sm:block" />}
              {next && (
                <Link to={docPath(next)} className="group border border-white/10 bg-white/[0.02] p-4 hover:border-[#5B8CFF] transition-colors sm:text-right">
                  <span className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-400">Next →</span>
                  <span className="block mt-1.5 text-white font-medium group-hover:text-[#5B8CFF] transition-colors">{DOCS[next].title}</span>
                </Link>
              )}
            </nav>
          )}

          <p className="mt-10 text-sm text-neutral-500">
            Still stuck? Email{" "}
            <a href="mailto:ops@ownpbx.in" className="text-white underline underline-offset-4 hover:text-[#5B8CFF]">ops@ownpbx.in</a>.
          </p>
        </article>

        <OnThisPage sections={doc.sections} />
      </div>
    </div>
  );
}
